import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  useColorScheme,
  Dimensions,
  StyleSheet,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { BarChart } from 'react-native-chart-kit';
import WebECharts from '../components/WebECharts';
import ECharts from '../components/ECharts';

const screenWidth = Dimensions.get('window').width;
// 根据平台选择合适的图表组件
const ChartComponent = Platform.OS === 'web' ? WebECharts : ECharts;

const TimeAnalysisScreen = ({ navigation }) => {
  const isDarkMode = useColorScheme() === 'dark';
  const [activeTab, setActiveTab] = useState('dailyTrends');
  const [selectedNutrient, setSelectedNutrient] = useState('calories');
  
  // 模拟数据 - 在实际项目中应从API或本地存储获取
  const [nutritionData, setNutritionData] = useState({
    // 每日营养素摄入数据 - 最近60天
    dailyIntake: Array.from({ length: 60 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - 59 + i);
      const dateStr = `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      
      // 添加微小的趋势因素，使数据有轻微的上升趋势
      const trendFactor = 1 + (i * 0.0005); // 添加非常小的递增趋势
      // 随机生成一些变化的数值，但保持在合理范围内
      const variance = Math.random() * 0.06 + 0.97; // 在97%~103%之间波动，进一步减小随机波动
      const dayOfWeek = date.getDay(); // 0是周日，6是周六
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const weekendFactor = isWeekend ? 1.03 : 1; // 周末摄入量略高，进一步减小差异
      
      // 周期性变化因素（模拟每周规律）
      const weekCycleFactor = 1 + 0.01 * Math.sin(i * Math.PI / 7);
      
      return {
        date: dateStr,
        calories: Math.round(2000 * variance * weekendFactor * trendFactor * weekCycleFactor),
        protein: Math.round(100 * variance * weekendFactor * trendFactor * weekCycleFactor),
        carbs: Math.round(250 * variance * weekendFactor * trendFactor * weekCycleFactor),
        fat: Math.round(70 * variance * weekendFactor * trendFactor * weekCycleFactor),
        vitamins: Math.round(800 * variance * weekendFactor * trendFactor * weekCycleFactor),
        fiber: Math.round(25 * variance * weekendFactor * trendFactor * weekCycleFactor),
      };
    }),
    
    // 餐次分布数据 - 更加平衡的分布
    mealDistribution: [
      { meal: '早餐', calories: 520, protein: 26, carbs: 72, fat: 16, vitamins: 210, fiber: 6 },
      { meal: '上午加餐', calories: 150, protein: 8, carbs: 18, fat: 6, vitamins: 80, fiber: 2 },
      { meal: '午餐', calories: 680, protein: 34, carbs: 82, fat: 24, vitamins: 240, fiber: 8 },
      { meal: '下午加餐', calories: 180, protein: 9, carbs: 22, fat: 8, vitamins: 90, fiber: 3 },
      { meal: '晚餐', calories: 620, protein: 31, carbs: 76, fat: 21, vitamins: 230, fiber: 7 },
      { meal: '夜宵', calories: 150, protein: 8, carbs: 18, fat: 7, vitamins: 50, fiber: 1 },
    ],
    
    // 工作日vs周末vs假期对比 - 减小差异
    weekdayWeekend: {
      weekday: { calories: 1950, protein: 97, carbs: 245, fat: 67, vitamins: 760, fiber: 23 },
      weekend: { calories: 2050, protein: 102, carbs: 255, fat: 72, vitamins: 790, fiber: 24 },
      holiday: { calories: 2150, protein: 107, carbs: 265, fat: 77, vitamins: 820, fiber: 22 },
    },
    
    // 月度营养摄入模式 - 最近8个月，减小波动
    monthlyPattern: [
      { month: '9月', calories: 1950, protein: 97, carbs: 245, fat: 67, vitamins: 760, fiber: 23 },
      { month: '10月', calories: 1980, protein: 99, carbs: 248, fat: 68, vitamins: 770, fiber: 23 },
      { month: '11月', calories: 2020, protein: 101, carbs: 252, fat: 70, vitamins: 780, fiber: 24 },
      { month: '12月', calories: 2050, protein: 102, carbs: 255, fat: 72, vitamins: 790, fiber: 24 },
      { month: '1月', calories: 2030, protein: 101, carbs: 253, fat: 71, vitamins: 785, fiber: 24 },
      { month: '2月', calories: 2000, protein: 100, carbs: 250, fat: 70, vitamins: 775, fiber: 23 },
      { month: '3月', calories: 1980, protein: 99, carbs: 248, fat: 69, vitamins: 770, fiber: 23 },
      { month: '4月', calories: 1990, protein: 99, carbs: 249, fat: 70, vitamins: 775, fiber: 24 },
    ],
    
    // 活动水平与营养摄入关系 - 更合理的递增
    activityNutrition: [
      { level: '轻度活动', calories: 1900, protein: 95, carbs: 235, fat: 65 },
      { level: '中度活动', calories: 2100, protein: 105, carbs: 260, fat: 72 },
      { level: '重度活动', calories: 2300, protein: 115, carbs: 285, fat: 80 },
    ],
    
    // 年龄段营养需求对比 - 更平滑的递减
    ageNutrition: [
      { age: '18-30岁', calories: 2150, protein: 107, carbs: 268, fat: 74, vitamins: 820, fiber: 26 },
      { age: '31-50岁', calories: 2000, protein: 100, carbs: 250, fat: 70, vitamins: 780, fiber: 24 },
      { age: '51-70岁', calories: 1850, protein: 92, carbs: 232, fat: 65, vitamins: 740, fiber: 22 },
    ],
  });
  
  // 获取最近7天的数据用于趋势图
  const getRecentData = (days = 7) => {
    return nutritionData.dailyIntake.slice(-days);
  };
  
  // 图表配置
  const chartConfig = {
    backgroundGradientFrom: isDarkMode ? '#a9b6d0' : '#ffffff',
    backgroundGradientTo: isDarkMode ? '#a9b6d0' : '#ffffff',
    color: (opacity = 1) => isDarkMode ? `rgba(255, 255, 255, ${opacity})` : `rgba(0, 0, 0, ${opacity})`,
    strokeWidth: 2,
    decimalPlaces: 0,
    style: {
      borderRadius: 16,
    },
    propsForDots: {
      r: "4",
      strokeWidth: "2",
      stroke: "#2563eb"
    }
  };
  
  // 准备营养素趋势图数据
  const getNutrientTrendData = (nutrient, days = 7) => {
    const recentData = getRecentData(days);
    
    // 根据所选营养素获取相应数据和颜色
    let color;
    switch(nutrient) {
      case 'protein':
        color = '#10b981'; // 绿色
        break;
      case 'carbs':
        color = '#f59e0b'; // 黄色
        break;
      case 'fat':
        color = '#ec4899'; // 粉色
        break;
      case 'vitamins':
        color = '#8b5cf6'; // 紫色
        break;
      case 'fiber':
        color = '#14b8a6'; // 青色
        break;
      default:
        color = '#2563eb'; // 蓝色(默认，卡路里)
    }
    
    return {
      labels: recentData.map(item => item.date.split('-')[1]), // 只显示日期部分
      datasets: [
        {
          data: recentData.map(item => item[nutrient]),
          color: (opacity = 1) => `rgba(${color.replace(/^#/, '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, ${opacity})`,
          strokeWidth: 2,
        }
      ],
      legend: [getNutrientLabel(nutrient)]
    };
  };
  
  // 准备月度营养摄入模式数据 - ECharts格式
  const getMonthlyPatternDataECharts = (nutrient = 'calories') => {
    // 根据所选营养素获取相应数据和颜色
    let color;
    switch(nutrient) {
      case 'protein':
        color = '#10b981'; // 绿色
        break;
      case 'carbs':
        color = '#f59e0b'; // 黄色
        break;
      case 'fat':
        color = '#ec4899'; // 粉色
        break;
      case 'vitamins':
        color = '#8b5cf6'; // 紫色
        break;
      case 'fiber':
        color = '#14b8a6'; // 青色
        break;
      default:
        color = '#2563eb'; // 蓝色(默认，卡路里)
    }
    
    return {
      tooltip: {
        trigger: 'axis',
        formatter: function(params) {
          return params[0].name + '<br/>' + 
                 params[0].marker + ' ' + 
                 getNutrientLabel(nutrient) + ': ' + 
                 params[0].value;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '8%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: nutritionData.monthlyPattern.map(item => item.month),
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        },
        splitLine: {
          lineStyle: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      series: [{
        name: getNutrientLabel(nutrient),
        data: nutritionData.monthlyPattern.map(item => item[nutrient]),
        type: 'line',
        smooth: true,
        symbolSize: 6,
        itemStyle: {
          color: color
        },
        lineStyle: {
          width: 3,
          color: color
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0,
              color: color + 'CC' // 带透明度的颜色
            }, {
              offset: 1,
              color: color + '11'
            }]
          }
        }
      }]
    };
  };
  
  // 准备营养素趋势图数据 - ECharts格式
  const getNutrientTrendDataECharts = (nutrient, days = 14) => {
    const recentData = getRecentData(days);
    
    // 根据所选营养素获取相应数据和颜色
    let color;
    switch(nutrient) {
      case 'protein':
        color = '#10b981'; // 绿色
        break;
      case 'carbs':
        color = '#f59e0b'; // 黄色
        break;
      case 'fat':
        color = '#ec4899'; // 粉色
        break;
      case 'vitamins':
        color = '#8b5cf6'; // 紫色
        break;
      case 'fiber':
        color = '#14b8a6'; // 青色
        break;
      default:
        color = '#2563eb'; // 蓝色(默认，卡路里)
    }
    
    return {
      tooltip: {
        trigger: 'axis',
        formatter: function(params) {
          return params[0].name + '<br/>' + 
                 params[0].marker + ' ' + 
                 getNutrientLabel(nutrient) + ': ' + 
                 params[0].value;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '8%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: recentData.map(item => item.date.split('-')[1] + '日'),
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        },
        splitLine: {
          lineStyle: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      series: [{
        name: getNutrientLabel(nutrient),
        data: recentData.map(item => item[nutrient]),
        type: 'line',
        smooth: true,
        showSymbol: false,
        lineStyle: {
          width: 3,
          color: color
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0,
              color: color + 'CC' // 带透明度的颜色
            }, {
              offset: 1,
              color: color + '11'
            }]
          }
        }
      }]
    };
  };
  
  // 准备餐次分布图数据 - ECharts格式
  const getMealDistributionDataECharts = (nutrient = 'calories') => {
    // 根据所选营养素获取相应数据和颜色
    let color;
    switch(nutrient) {
      case 'protein':
        color = '#10b981'; // 绿色
        break;
      case 'carbs':
        color = '#f59e0b'; // 黄色
        break;
      case 'fat':
        color = '#ec4899'; // 粉色
        break;
      case 'vitamins':
        color = '#8b5cf6'; // 紫色
        break;
      case 'fiber':
        color = '#14b8a6'; // 青色
        break;
      default:
        color = '#2563eb'; // 蓝色(默认，卡路里)
    }
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params) {
          return params[0].name + '<br/>' + 
                 params[0].marker + ' ' + 
                 getNutrientLabel(nutrient) + ': ' + 
                 params[0].value;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '8%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: nutritionData.mealDistribution.map(item => item.meal),
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10,
          interval: 0,
          rotate: nutritionData.mealDistribution.length > 4 ? 30 : 0
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        },
        splitLine: {
          lineStyle: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      series: [{
        name: getNutrientLabel(nutrient),
        data: nutritionData.mealDistribution.map(item => item[nutrient]),
        type: 'bar',
        showBackground: true,
        backgroundStyle: {
          color: 'rgba(180, 180, 180, 0.2)'
        },
        itemStyle: {
          color: color,
          borderRadius: [4, 4, 0, 0]
        },
        emphasis: {
          itemStyle: {
            color: color.replace('#', '#cc')
          }
        },
        label: {
          show: true,
          position: 'top',
          formatter: '{c}',
          fontSize: 10,
          color: isDarkMode ? '#e0e0e0' : '#666'
        }
      }]
    };
  };
  
  // 准备工作日vs周末对比数据 - ECharts格式
  const getWeekdayWeekendDataECharts = () => {
    const categories = ['卡路里', '蛋白质', '碳水', '脂肪'];
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: ['工作日', '周末', '假期'],
        textStyle: {
          color: isDarkMode ? '#e0e0e0' : '#666'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        },
        splitLine: {
          lineStyle: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      series: [
        {
          name: '工作日',
          type: 'bar',
          data: [
            nutritionData.weekdayWeekend.weekday.calories,
            nutritionData.weekdayWeekend.weekday.protein,
            nutritionData.weekdayWeekend.weekday.carbs,
            nutritionData.weekdayWeekend.weekday.fat,
          ],
          itemStyle: {
            color: '#2563eb',
            borderRadius: [4, 4, 0, 0]
          },
          showBackground: true,
          backgroundStyle: {
            color: 'rgba(180, 180, 180, 0.2)'
          },
          label: {
            show: true,
            position: 'top',
            fontSize: 10,
            color: isDarkMode ? '#e0e0e0' : '#666'
          }
        },
        {
          name: '周末',
          type: 'bar',
          data: [
            nutritionData.weekdayWeekend.weekend.calories,
            nutritionData.weekdayWeekend.weekend.protein,
            nutritionData.weekdayWeekend.weekend.carbs,
            nutritionData.weekdayWeekend.weekend.fat,
          ],
          itemStyle: {
            color: '#ec4899',
            borderRadius: [4, 4, 0, 0]
          },
          showBackground: true,
          backgroundStyle: {
            color: 'rgba(180, 180, 180, 0.2)'
          },
          label: {
            show: true,
            position: 'top',
            fontSize: 10,
            color: isDarkMode ? '#e0e0e0' : '#666'
          }
        },
        {
          name: '假期',
          type: 'bar',
          data: [
            nutritionData.weekdayWeekend.holiday.calories,
            nutritionData.weekdayWeekend.holiday.protein,
            nutritionData.weekdayWeekend.holiday.carbs,
            nutritionData.weekdayWeekend.holiday.fat,
          ],
          itemStyle: {
            color: '#f59e0b',
            borderRadius: [4, 4, 0, 0]
          },
          showBackground: true,
          backgroundStyle: {
            color: 'rgba(180, 180, 180, 0.2)'
          },
          label: {
            show: true,
            position: 'top',
            fontSize: 10,
            color: isDarkMode ? '#e0e0e0' : '#666'
          }
        }
      ]
    };
  };
  
  // 获取营养素的中文标签
  const getNutrientLabel = (nutrient) => {
    switch(nutrient) {
      case 'protein':
        return '蛋白质(克)';
      case 'carbs':
        return '碳水(克)';
      case 'fat':
        return '脂肪(克)';
      case 'vitamins':
        return '维生素(毫克)';
      case 'fiber':
        return '膳食纤维(克)';
      default:
        return '卡路里(千卡)';
    }
  };
  
  // 准备活动水平与营养摄入关系数据 - ECharts格式
  const getActivityNutritionDataECharts = (nutrient = 'calories') => {
    // 根据所选营养素获取相应数据和颜色
    let color;
    switch(nutrient) {
      case 'protein':
        color = '#10b981'; // 绿色
        break;
      case 'carbs':
        color = '#f59e0b'; // 黄色
        break;
      case 'fat':
        color = '#ec4899'; // 粉色
        break;
      case 'vitamins':
        color = '#8b5cf6'; // 紫色
        break;
      case 'fiber':
        color = '#14b8a6'; // 青色
        break;
      default:
        color = '#2563eb'; // 蓝色(默认，卡路里)
    }
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params) {
          return params[0].name + '<br/>' + 
                 params[0].marker + ' ' + 
                 getNutrientLabel(nutrient) + ': ' + 
                 params[0].value;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '8%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: nutritionData.activityNutrition.map(item => item.level),
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        },
        splitLine: {
          lineStyle: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      series: [{
        name: getNutrientLabel(nutrient),
        data: nutritionData.activityNutrition.map(item => item[nutrient]),
        type: 'bar',
        showBackground: true,
        backgroundStyle: {
          color: 'rgba(180, 180, 180, 0.2)'
        },
        itemStyle: {
          color: color,
          borderRadius: [4, 4, 0, 0]
        },
        emphasis: {
          itemStyle: {
            color: color.replace('#', '#cc')
          }
        },
        label: {
          show: true,
          position: 'top',
          formatter: '{c}',
          fontSize: 10,
          color: isDarkMode ? '#e0e0e0' : '#666'
        }
      }]
    };
  };
  
  // 准备年龄段营养需求对比数据 - ECharts格式
  const getAgeNutritionDataECharts = (nutrient = 'calories') => {
    // 根据所选营养素获取相应数据和颜色
    let color = '#8b5cf6'; // 紫色为默认
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params) {
          return params[0].name + '<br/>' + 
                 params[0].marker + ' ' + 
                 getNutrientLabel(nutrient) + ': ' + 
                 params[0].value;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '8%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: nutritionData.ageNutrition.map(item => item.age),
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        },
        splitLine: {
          lineStyle: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      series: [{
        name: getNutrientLabel(nutrient),
        data: nutritionData.ageNutrition.map(item => item[nutrient]),
        type: 'bar',
        showBackground: true,
        backgroundStyle: {
          color: 'rgba(180, 180, 180, 0.2)'
        },
        itemStyle: {
          color: color,
          borderRadius: [4, 4, 0, 0]
        },
        emphasis: {
          itemStyle: {
            color: color.replace('#', '#cc')
          }
        },
        label: {
          show: true,
          position: 'top',
          formatter: '{c}',
          fontSize: 10,
          color: isDarkMode ? '#e0e0e0' : '#666'
        }
      }]
    };
  };
  
  return (
    <ScrollView 
      style={{
        flex: 1,
        backgroundColor: isDarkMode ? '#8fa0c1' : '#f9fafb',
      }}
      contentContainerStyle={{ padding: 16 }}
    >
      {/* 页面导航标签 */}
      <View style={{
        flexDirection: 'row',
        marginBottom: 16,
        backgroundColor: isDarkMode ? '#a9b6d0' : '#ffffff',
        borderRadius: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
        elevation: 2,
      }}>
        <TouchableOpacity
          style={{
            flex: 1,
            paddingVertical: 12,
            alignItems: 'center',
            borderBottomWidth: activeTab === 'dailyTrends' ? 2 : 0,
            borderBottomColor: '#2563eb',
          }}
          onPress={() => setActiveTab('dailyTrends')}
        >
          <Text style={{
            fontWeight: activeTab === 'dailyTrends' ? 'bold' : 'normal',
            color: activeTab === 'dailyTrends' ? '#2563eb' : (isDarkMode ? '#e0e0e0' : '#6b7280'),
          }}>
            每日趋势
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={{
            flex: 1,
            paddingVertical: 12,
            alignItems: 'center',
            borderBottomWidth: activeTab === 'periodicalAnalysis' ? 2 : 0,
            borderBottomColor: '#2563eb',
          }}
          onPress={() => setActiveTab('periodicalAnalysis')}
        >
          <Text style={{
            fontWeight: activeTab === 'periodicalAnalysis' ? 'bold' : 'normal',
            color: activeTab === 'periodicalAnalysis' ? '#2563eb' : (isDarkMode ? '#e0e0e0' : '#6b7280'),
          }}>
            周期分析
          </Text>
        </TouchableOpacity>
      </View>
      
      {activeTab === 'dailyTrends' ? (
        <>
          {/* 每日营养摄入趋势 */}
          <View style={{
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
            backgroundColor: isDarkMode ? '#a9b6d0' : '#ffffff',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 2,
          }}>
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              每日营养摄入趋势
            </Text>
            
            {/* 营养素选择器 */}
            <View style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              marginBottom: 16,
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 8,
            }}>
              <TouchableOpacity
                style={{
                  width: '33%',
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  borderRadius: 6,
                  backgroundColor: selectedNutrient === 'calories' ? '#2563eb' : 'transparent',
                  alignItems: 'center',
                  marginBottom: 4,
                }}
                onPress={() => setSelectedNutrient('calories')}
              >
                <Text style={{
                  fontWeight: selectedNutrient === 'calories' ? 'bold' : 'normal',
                  color: selectedNutrient === 'calories' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  fontSize: 12,
                }}>
                  卡路里
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={{
                  width: '33%',
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  borderRadius: 6,
                  backgroundColor: selectedNutrient === 'protein' ? '#10b981' : 'transparent',
                  alignItems: 'center',
                  marginBottom: 4,
                }}
                onPress={() => setSelectedNutrient('protein')}
              >
                <Text style={{
                  fontWeight: selectedNutrient === 'protein' ? 'bold' : 'normal',
                  color: selectedNutrient === 'protein' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  fontSize: 12,
                }}>
                  蛋白质
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={{
                  width: '33%',
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  borderRadius: 6,
                  backgroundColor: selectedNutrient === 'carbs' ? '#f59e0b' : 'transparent',
                  alignItems: 'center',
                  marginBottom: 4,
                }}
                onPress={() => setSelectedNutrient('carbs')}
              >
                <Text style={{
                  fontWeight: selectedNutrient === 'carbs' ? 'bold' : 'normal',
                  color: selectedNutrient === 'carbs' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  fontSize: 12,
                }}>
                  碳水
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={{
                  width: '33%',
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  borderRadius: 6,
                  backgroundColor: selectedNutrient === 'fat' ? '#ec4899' : 'transparent',
                  alignItems: 'center',
                }}
                onPress={() => setSelectedNutrient('fat')}
              >
                <Text style={{
                  fontWeight: selectedNutrient === 'fat' ? 'bold' : 'normal',
                  color: selectedNutrient === 'fat' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  fontSize: 12,
                }}>
                  脂肪
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={{
                  width: '33%',
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  borderRadius: 6,
                  backgroundColor: selectedNutrient === 'vitamins' ? '#8b5cf6' : 'transparent',
                  alignItems: 'center',
                }}
                onPress={() => setSelectedNutrient('vitamins')}
              >
                <Text style={{
                  fontWeight: selectedNutrient === 'vitamins' ? 'bold' : 'normal',
                  color: selectedNutrient === 'vitamins' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  fontSize: 12,
                }}>
                  维生素
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={{
                  width: '33%',
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  borderRadius: 6,
                  backgroundColor: selectedNutrient === 'fiber' ? '#14b8a6' : 'transparent',
                  alignItems: 'center',
                }}
                onPress={() => setSelectedNutrient('fiber')}
              >
                <Text style={{
                  fontWeight: selectedNutrient === 'fiber' ? 'bold' : 'normal',
                  color: selectedNutrient === 'fiber' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  fontSize: 12,
                }}>
                  膳食纤维
                </Text>
              </TouchableOpacity>
            </View>
            
            {/* 营养素趋势图 */}
            <View style={{
              marginBottom: 20,
              alignItems: 'center',
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 8,
            }}>
              <ChartComponent
                option={getNutrientTrendDataECharts(selectedNutrient, 14)}
                height={220}
                width={screenWidth - 48}
              />
            </View>
            
            {/* 餐次分布柱状图 */}
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              marginBottom: 8,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              餐次分布柱状图
            </Text>
            <View style={{
              marginBottom: 12,
              alignItems: 'center',
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 8,
            }}>
              <ChartComponent
                option={getMealDistributionDataECharts(selectedNutrient)}
                height={220}
                width={screenWidth - 48}
              />
            </View>
          </View>
        </>
      ) : (
        <>
          {/* 周期性分析 */}
          <View style={{
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
            backgroundColor: isDarkMode ? '#a9b6d0' : '#ffffff',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 2,
          }}>
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              工作日vs周末对比
            </Text>
            
            {/* 工作日vs周末对比 */}
            <View style={{
              marginBottom: 24,
              alignItems: 'center',
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 8,
            }}>
              <ChartComponent
                option={getWeekdayWeekendDataECharts()}
                height={220}
                width={screenWidth - 48}
              />
            </View>
            
            {/* 工作日vs周末分析说明 */}
            <View style={{
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 16,
              marginBottom: 24,
            }}>
              <Text style={{
                fontSize: 14,
                color: isDarkMode ? '#e0e0e0' : '#4b5563',
                lineHeight: 20,
              }}>
                分析显示您在周末摄入的卡路里比工作日平均高出约{' '}
                <Text style={{ fontWeight: 'bold', color: '#2563eb' }}>
                  {nutritionData.weekdayWeekend.weekend.calories - nutritionData.weekdayWeekend.weekday.calories}千卡
                </Text>
                （
                {Math.round((nutritionData.weekdayWeekend.weekend.calories / nutritionData.weekdayWeekend.weekday.calories - 1) * 100)}%
                ）。蛋白质、碳水和脂肪摄入也有类似趋势。假期期间摄入量进一步增加，尤其是碳水和脂肪的摄入。
              </Text>
            </View>
            
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              月度营养摄入模式分析
            </Text>
            
            {/* 营养素选择器 */}
            <View style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              marginBottom: 16,
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 8,
            }}>
              <TouchableOpacity
                style={{
                  width: '33%',
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  borderRadius: 6,
                  backgroundColor: selectedNutrient === 'calories' ? '#2563eb' : 'transparent',
                  alignItems: 'center',
                  marginBottom: 4,
                }}
                onPress={() => setSelectedNutrient('calories')}
              >
                <Text style={{
                  fontWeight: selectedNutrient === 'calories' ? 'bold' : 'normal',
                  color: selectedNutrient === 'calories' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  fontSize: 12,
                }}>
                  卡路里
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={{
                  width: '33%',
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  borderRadius: 6,
                  backgroundColor: selectedNutrient === 'protein' ? '#10b981' : 'transparent',
                  alignItems: 'center',
                  marginBottom: 4,
                }}
                onPress={() => setSelectedNutrient('protein')}
              >
                <Text style={{
                  fontWeight: selectedNutrient === 'protein' ? 'bold' : 'normal',
                  color: selectedNutrient === 'protein' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  fontSize: 12,
                }}>
                  蛋白质
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={{
                  width: '33%',
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  borderRadius: 6,
                  backgroundColor: selectedNutrient === 'carbs' ? '#f59e0b' : 'transparent',
                  alignItems: 'center',
                  marginBottom: 4,
                }}
                onPress={() => setSelectedNutrient('carbs')}
              >
                <Text style={{
                  fontWeight: selectedNutrient === 'carbs' ? 'bold' : 'normal',
                  color: selectedNutrient === 'carbs' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  fontSize: 12,
                }}>
                  碳水
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={{
                  width: '33%',
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  borderRadius: 6,
                  backgroundColor: selectedNutrient === 'fat' ? '#ec4899' : 'transparent',
                  alignItems: 'center',
                }}
                onPress={() => setSelectedNutrient('fat')}
              >
                <Text style={{
                  fontWeight: selectedNutrient === 'fat' ? 'bold' : 'normal',
                  color: selectedNutrient === 'fat' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  fontSize: 12,
                }}>
                  脂肪
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={{
                  width: '33%',
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  borderRadius: 6,
                  backgroundColor: selectedNutrient === 'vitamins' ? '#8b5cf6' : 'transparent',
                  alignItems: 'center',
                }}
                onPress={() => setSelectedNutrient('vitamins')}
              >
                <Text style={{
                  fontWeight: selectedNutrient === 'vitamins' ? 'bold' : 'normal',
                  color: selectedNutrient === 'vitamins' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  fontSize: 12,
                }}>
                  维生素
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={{
                  width: '33%',
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  borderRadius: 6,
                  backgroundColor: selectedNutrient === 'fiber' ? '#14b8a6' : 'transparent',
                  alignItems: 'center',
                }}
                onPress={() => setSelectedNutrient('fiber')}
              >
                <Text style={{
                  fontWeight: selectedNutrient === 'fiber' ? 'bold' : 'normal',
                  color: selectedNutrient === 'fiber' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
                  fontSize: 12,
                }}>
                  膳食纤维
                </Text>
              </TouchableOpacity>
            </View>
            
            {/* 月度营养摄入模式图 */}
            <View style={{
              marginBottom: 20,
              alignItems: 'center',
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 8,
            }}>
              <ChartComponent
                option={getMonthlyPatternDataECharts(selectedNutrient)}
                height={220}
                width={screenWidth - 48}
              />
            </View>
            
            {/* 月度模式分析说明 */}
            <View style={{
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 16,
              marginBottom: 24,
            }}>
              <Text style={{
                fontSize: 14,
                color: isDarkMode ? '#e0e0e0' : '#4b5563',
                lineHeight: 20,
              }}>
                您的{getNutrientLabel(selectedNutrient).split('(')[0]}摄入在12月达到峰值，
                可能与冬季和节假日饮食习惯有关。最近几个月趋于平稳，
                保持在健康范围内，显示出良好的饮食规律性。
                从9月至今的8个月数据来看，您的{getNutrientLabel(selectedNutrient).split('(')[0]}摄入整体呈现先上升后稳定的趋势。
              </Text>
            </View>
            
            {/* 活动水平与营养摄入关系 */}
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              活动水平与营养摄入关系
            </Text>
            
            <View style={{
              marginBottom: 20,
              alignItems: 'center',
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 8,
            }}>
              <ChartComponent
                option={getActivityNutritionDataECharts(selectedNutrient)}
                height={220}
                width={screenWidth - 48}
              />
            </View>
            
            {/* 活动水平分析说明 */}
            <View style={{
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 16,
              marginBottom: 24,
            }}>
              <Text style={{
                fontSize: 14,
                color: isDarkMode ? '#e0e0e0' : '#4b5563',
                lineHeight: 20,
              }}>
                数据显示，随着活动水平的增加，您的{getNutrientLabel(selectedNutrient).split('(')[0]}需求也相应增加。
                重度活动时期比轻度活动时期的{getNutrientLabel(selectedNutrient).split('(')[0]}摄入高出约
                <Text style={{ fontWeight: 'bold', color: '#2563eb' }}>
                  {nutritionData.activityNutrition[2][selectedNutrient] - nutritionData.activityNutrition[0][selectedNutrient]}
                  {selectedNutrient === 'calories' ? '千卡' : '克'}
                </Text>，
                这符合身体在运动时对能量和营养素的额外需求。
              </Text>
            </View>
            
            {/* 年龄段营养需求对比 */}
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              年龄段营养需求对比
            </Text>
            
            <View style={{
              marginBottom: 20,
              alignItems: 'center',
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 8,
            }}>
              <ChartComponent
                option={getAgeNutritionDataECharts(selectedNutrient)}
                height={220}
                width={screenWidth - 48}
              />
            </View>
            
            {/* 年龄段分析说明 */}
            <View style={{
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 16,
            }}>
              <Text style={{
                fontSize: 14,
                color: isDarkMode ? '#e0e0e0' : '#4b5563',
                lineHeight: 20,
              }}>
                随着年龄的增长，身体的基础代谢率逐渐降低，{getNutrientLabel(selectedNutrient).split('(')[0]}需求也相应减少。
                与年轻人相比，中年和老年人需要更注重营养的质量而非数量，以维持健康的身体状况。
                建议根据自身年龄段调整饮食结构，确保摄入足够的营养。
              </Text>
            </View>
          </View>
        </>
      )}
    </ScrollView>
  );
};

export default TimeAnalysisScreen; 